/**
 * Dashboard Page
 * Rich dashboard with charts, risk overview, SLA compliance, and recent activity.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketApi } from '../api/client';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  AlertTriangle, Clock, ShieldAlert, TrendingUp,
  Activity, ChevronRight, Shield, CheckCircle2, XCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const PRIORITY_COLORS = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };
const STATUS_COLORS = {
  new: '#3b82f6', open: '#10b981', in_progress: '#f59e0b', escalated: '#ef4444',
  on_hold: '#6b7280', pending_client: '#a855f7', resolved: '#059669', closed: '#4b5563',
};
const CATEGORY_COLORS = ['#6366f1', '#14b8a6', '#f97316', '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16', '#e11d48'];

function StatCard({ icon: Icon, label, value, sublabel, color = 'gray', alert = false }) {
  const colorMap = {
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    gray: 'bg-gray-800/70 text-gray-300 border-gray-700/60',
  };
  return (
    <div className={`rounded-xl border p-5 ${colorMap[color]} ${alert ? 'ring-1 ring-red-500/30' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <Icon className="w-5 h-5 opacity-70" />
        {alert && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span></span>}
      </div>
      <div className="text-3xl font-semibold tracking-tight">{value}</div>
      <div className="text-sm mt-1 opacity-70">{label}</div>
      {sublabel && <div className="text-xs mt-2 opacity-50">{sublabel}</div>}
    </div>
  );
}

function RiskGauge({ score }) {
  const getColor = (s) => s >= 75 ? '#ef4444' : s >= 50 ? '#f59e0b' : s >= 25 ? '#3b82f6' : '#10b981';
  const getLabel = (s) => s >= 75 ? 'Critical' : s >= 50 ? 'Elevated' : s >= 25 ? 'Moderate' : 'Low';
  return (
    <div className="flex flex-col items-center justify-center py-2">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-800" />
          <circle cx="60" cy="60" r="50" fill="none" stroke={getColor(score)} strokeWidth="8"
            strokeDasharray={`${score * 3.14} 314`} strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color: getColor(score) }}>{score}</span>
          <span className="text-[10px] uppercase tracking-wider text-gray-500">{getLabel(score)}</span>
        </div>
      </div>
    </div>
  );
}

function SlaRing({ rate, label, met, missed }) {
  const color = rate >= 90 ? '#10b981' : rate >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-20 h-20 mb-2">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
          <circle cx="40" cy="40" r="32" fill="none" stroke="currentColor" strokeWidth="6" className="text-gray-800" />
          <circle cx="40" cy="40" r="32" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${rate * 2.01} 201`} strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold" style={{ color }}>{rate}%</span>
        </div>
      </div>
      <span className="text-xs text-gray-400 font-medium">{label}</span>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
        <span className="flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" />{met}</span>
        <span className="flex items-center gap-0.5"><XCircle className="w-3 h-3 text-red-400" />{missed}</span>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-gray-400 mb-0.5">{payload[0]?.name}</p>
      <p className="text-sm font-semibold text-gray-200">{payload[0]?.value} tickets</p>
    </div>
  );
}

export default function DashboardPage() {
  const { user, tenant, isSentinelUser } = useAuth();
  const navigate = useNavigate();
  const basePath = isSentinelUser ? '/sentinel' : '/portal';
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const tid = isSentinelUser ? undefined : tenant?.id;
        const [summaryRes, chartsRes] = await Promise.all([
          ticketApi.getDashboard(tid),
          ticketApi.getDashboardCharts(tid),
        ]);
        setSummary(summaryRes.data);
        setCharts(chartsRes.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    }
    if (isSentinelUser || tenant?.id) load();
  }, [tenant?.id, isSentinelUser]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-800 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-gray-800 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-64 bg-gray-800 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const priorityData = (charts?.by_priority || []).map(d => ({
    ...d, fill: PRIORITY_COLORS[d.priority] || '#6b7280',
    name: d.priority.charAt(0).toUpperCase() + d.priority.slice(1),
  }));
  const statusData = (charts?.by_status || []).map(d => ({
    ...d, fill: STATUS_COLORS[d.status] || '#6b7280',
    name: d.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  }));
  const categoryData = (charts?.by_category || []).map((d, i) => ({
    ...d, fill: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    name: d.category.charAt(0).toUpperCase() + d.category.slice(1),
  }));
  const sla = charts?.sla_compliance || {};

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">
          {isSentinelUser ? 'Operations Dashboard' : tenant?.name || 'Dashboard'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isSentinelUser ? 'Cross-tenant operations overview' : 'Your technology environment at a glance'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={AlertTriangle} label="Open tickets" value={summary?.open_tickets || 0}
          sublabel={`${summary?.new_tickets || 0} new, awaiting triage`}
          color={summary?.open_tickets > 10 ? 'amber' : 'gray'} />
        <StatCard icon={ShieldAlert} label="Critical open" value={summary?.critical_open || 0}
          color={summary?.critical_open > 0 ? 'red' : 'emerald'} alert={summary?.critical_open > 0} />
        <StatCard icon={Clock} label="SLA breaches"
          value={(summary?.sla_response_breached || 0) + (summary?.sla_resolution_breached || 0)}
          sublabel={`${summary?.sla_response_breached || 0} response, ${summary?.sla_resolution_breached || 0} resolution`}
          color={(summary?.sla_response_breached || 0) + (summary?.sla_resolution_breached || 0) > 0 ? 'red' : 'emerald'}
          alert={(summary?.sla_response_breached || 0) + (summary?.sla_resolution_breached || 0) > 0} />
        <StatCard icon={TrendingUp} label="Escalated" value={summary?.escalated_tickets || 0}
          sublabel="Requiring senior attention" color={summary?.escalated_tickets > 0 ? 'amber' : 'gray'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-300">Risk posture</h3>
          </div>
          <RiskGauge score={Math.round(summary?.avg_risk_score || 0)} />
          <p className="text-xs text-gray-500 text-center mt-2">Average risk across open tickets</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-300">By priority</h3>
          </div>
          {priorityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={priorityData} dataKey="count" nameKey="name" cx="50%" cy="50%"
                  innerRadius={45} outerRadius={70} paddingAngle={3} strokeWidth={0}>
                  {priorityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8}
                  formatter={(val) => <span className="text-xs text-gray-400">{val}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-sm text-gray-600">No open tickets</div>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-300">SLA compliance</h3>
          </div>
          <div className="flex items-center justify-around h-[180px]">
            <SlaRing rate={sla.response_rate || 0} label="Response"
              met={sla.response_met || 0} missed={sla.response_missed || 0} />
            <SlaRing rate={sla.resolution_rate || 0} label="Resolution"
              met={sla.resolution_met || 0} missed={sla.resolution_missed || 0} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-300 mb-4">By category</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={categoryData} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={80}
                  tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={16}>
                  {categoryData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-gray-600">No categorized tickets</div>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-300 mb-4">By status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusData} layout="vertical" margin={{ left: 20, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={90}
                  tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={16}>
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-gray-600">No tickets</div>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-sm font-medium text-gray-300 mb-4">Highest risk tickets</h3>
          <div className="space-y-2">
            {(charts?.risk_items || []).slice(0, 6).map((item, i) => {
              const riskBg = item.score >= 75 ? 'bg-red-500/15' : item.score >= 50 ? 'bg-amber-500/15' : item.score >= 25 ? 'bg-blue-500/15' : 'bg-emerald-500/15';
              const riskText = item.score >= 75 ? 'text-red-400' : item.score >= 50 ? 'text-amber-400' : item.score >= 25 ? 'text-blue-400' : 'text-emerald-400';
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${riskBg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-xs font-bold ${riskText}`}>{item.score}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-300 truncate">{item.title}</div>
                    <div className="text-[10px] text-gray-500 font-mono">{item.ticket_number}</div>
                  </div>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ color: PRIORITY_COLORS[item.priority], backgroundColor: (PRIORITY_COLORS[item.priority] || '#6b7280') + '20' }}>
                    {item.priority}
                  </span>
                </div>
              );
            })}
            {(!charts?.risk_items || charts.risk_items.length === 0) && (
              <div className="h-[160px] flex items-center justify-center text-sm text-gray-600">No open tickets</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-300">Recent tickets</h3>
          </div>
          <button onClick={() => navigate(`${basePath}/tickets`)}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 transition-colors">
            View all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-1">
          {(charts?.recent_tickets || []).map((ticket) => {
            const priColor = PRIORITY_COLORS[ticket.priority] || '#6b7280';
            const riskColor = ticket.risk_score >= 75 ? 'text-red-400' : ticket.risk_score >= 50 ? 'text-amber-400' : ticket.risk_score >= 25 ? 'text-blue-400' : 'text-emerald-400';
            return (
              <div key={ticket.id} onClick={() => navigate(`${basePath}/tickets/${ticket.id}`)}
                className="flex items-center gap-4 px-3 py-2.5 rounded-lg hover:bg-gray-800/50 cursor-pointer transition-colors">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: priColor }} />
                <span className="text-xs text-gray-500 font-mono w-24 flex-shrink-0">{ticket.ticket_number}</span>
                <span className="text-sm text-gray-300 flex-1 truncate">{ticket.title}</span>
                <span className={`text-xs font-medium ${riskColor} w-8 text-right`}>{ticket.risk_score}</span>
                <span className="text-xs text-gray-600 w-24 text-right flex-shrink-0">
                  {ticket.created_at ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true }) : ''}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-gray-700 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
